import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, GripVertical, Type, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from './ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AboutEditor = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        setSections(res.data.about_sections || []);
      } catch (err) { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const autoSave = useCallback((updated) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/profile`, { about_sections: updated });
      } catch (err) { toast.error('Failed to save'); }
    }, 800);
  }, []);

  const updateSections = (updated) => {
    setSections(updated);
    autoSave(updated);
  };

  const addSection = (type) => {
    const newItem = type === 'text'
      ? { id: Date.now().toString(), type: 'text', heading: '', body: '' }
      : { id: Date.now().toString(), type: 'image', image_url: '', caption: '' };
    updateSections([...sections, newItem]);
  };

  const updateItem = (id, field, value) => {
    updateSections(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const removeItem = (id) => {
    updateSections(sections.filter(s => s.id !== id));
    toast.success('Section removed');
  };

  if (loading) {
    return (
      <div className="max-w-2xl flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Our Journey</h1>
          <p className="text-[#A1A1AA] mt-1">Tell your story with text and images</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-text-section" onClick={() => addSection('text')}
            className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-2 text-sm">
            <Type className="w-4 h-4" /> Add Text
          </Button>
          <Button data-testid="add-image-section" onClick={() => addSection('image')}
            variant="outline" className="rounded-full border-[#27272A] text-white hover:bg-white/5 gap-2 text-sm">
            <ImageIcon className="w-4 h-4" /> Add Image
          </Button>
        </div>
      </div>

      {sections.length === 0 ? (
        <div className="text-center py-16">
          <Type className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-white mb-2">No content yet</h3>
          <p className="text-[#A1A1AA] mb-6">Add text blocks and images to share your journey</p>
          <div className="flex justify-center gap-3">
            <Button data-testid="add-first-text" onClick={() => addSection('text')}
              className="rounded-full bg-violet-500 hover:bg-violet-600 text-white gap-2">
              <Type className="w-4 h-4" /> Add Text Block
            </Button>
            <Button data-testid="add-first-image" onClick={() => addSection('image')}
              variant="outline" className="rounded-full border-[#27272A] text-white hover:bg-white/5 gap-2">
              <ImageIcon className="w-4 h-4" /> Add Image
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} data-testid={`about-section-${section.id}`}
              className="rounded-2xl bg-[#121212] border border-[#27272A] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-[#71717A]" />
                  <span className="text-xs font-medium text-[#71717A] uppercase tracking-wider">
                    {section.type === 'text' ? 'Text Block' : 'Image'}
                  </span>
                </div>
                <button data-testid={`remove-about-${section.id}`} onClick={() => removeItem(section.id)}
                  className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>

              {section.type === 'text' ? (
                <div className="space-y-3">
                  <Input data-testid={`about-heading-${section.id}`}
                    value={section.heading || ''} onChange={(e) => updateItem(section.id, 'heading', e.target.value)}
                    className="bg-[#1E1E1E] border-[#27272A] text-white font-semibold"
                    placeholder="Section heading (e.g. Our Story, Mission)" />
                  <Textarea data-testid={`about-body-${section.id}`}
                    value={section.body || ''} onChange={(e) => updateItem(section.id, 'body', e.target.value)}
                    className="bg-[#1E1E1E] border-[#27272A] text-white resize-vertical min-h-[160px]"
                    placeholder="Write your story here..." rows={8}
                    style={{ height: 'auto', minHeight: '160px' }}
                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} />
                </div>
              ) : (
                <div className="space-y-3">
                  <ImageUpload value={section.image_url} onChange={(url) => updateItem(section.id, 'image_url', url)}
                    label="About Image" shape="rect" />
                  <Textarea data-testid={`about-caption-${section.id}`}
                    value={section.caption || ''} onChange={(e) => updateItem(section.id, 'caption', e.target.value)}
                    className="bg-[#1E1E1E] border-[#27272A] text-white resize-vertical min-h-[80px]"
                    placeholder="Image caption (optional)" rows={3}
                    onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AboutEditor;
